import { AlertCircle, Check, HelpCircle } from "lucide-react";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { cn } from "src/common/utils";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { MarkdownPreview } from "src/components/ui/MarkdownPreview";
import { useProductBranding } from "src/hooks/useProductBranding";

interface QuestionOptionType {
  label: string;
  description?: string;
}

interface QuestionType {
  question: string;
  header?: string;
  options: QuestionOptionType[];
  multiSelect?: boolean;
}

interface QuestionAnswerType {
  question_index: number;
  selected_options: number[];
  other_text?: string;
}

export interface AgentPendingQuestionType {
  toolUseId: string;
  questionsJson: {
    questions: QuestionType[];
  };
  answersJson?: QuestionAnswerType[];
  status?: "PENDING" | "TIMEOUT" | "ANSWERED";
}

interface AgentQuestionProps {
  question: AgentPendingQuestionType;
  onSubmit: (answers: QuestionAnswerType[]) => void;
  isSubmitting: boolean;
  isTimedOut?: boolean;
  /** When true, renders in a compact style for embedding in thought process */
  embedded?: boolean;
}

export const AgentQuestion: React.FC<AgentQuestionProps> = ({
  question,
  onSubmit,
  isSubmitting,
  isTimedOut = false,
  embedded = false,
}) => {
  const { productName } = useProductBranding();
  // Defensive parsing of questionsJson - handle cases where it might be malformed
  const questions = React.useMemo(() => {
    try {
      const q = question.questionsJson?.questions;
      return Array.isArray(q) ? q : [];
    } catch {
      return [];
    }
  }, [question.questionsJson]);

  const isAnswered = question.status === "ANSWERED";

  // answersJson is stored as {answers: [...]} in the database
  // Defensive parsing to handle various formats
  const submittedAnswers = React.useMemo(() => {
    try {
      if (Array.isArray(question.answersJson)) {
        return question.answersJson;
      }
      const answers = (question.answersJson as { answers?: QuestionAnswerType[] } | undefined)?.answers;
      return Array.isArray(answers) ? answers : undefined;
    } catch {
      return undefined;
    }
  }, [question.answersJson]);

  // State for each question's selections (used only when question is pending)
  // Map of question_index -> { selected_options: number[], other_text: string }
  const [localAnswers, setLocalAnswers] = useState<Record<number, { selected_options: number[]; other_text: string }>>(() => {
    const initial: Record<number, { selected_options: number[]; other_text: string }> = {};
    questions.forEach((_, idx) => {
      initial[idx] = { selected_options: [], other_text: "" };
    });
    return initial;
  });

  // Derive display answers from props when answered, or from local state when pending
  // This ensures Pusher updates showing the question is answered will display correctly
  const displayAnswers = useMemo(() => {
    if (isAnswered && submittedAnswers) {
      const fromSubmitted: Record<number, { selected_options: number[]; other_text: string }> = {};
      questions.forEach((_, idx) => {
        const submitted = submittedAnswers.find(a => a.question_index === idx);
        if (submitted) {
          fromSubmitted[idx] = {
            selected_options: submitted.selected_options || [],
            other_text: submitted.other_text || "",
          };
        } else {
          fromSubmitted[idx] = { selected_options: [], other_text: "" };
        }
      });
      return fromSubmitted;
    }
    return localAnswers;
  }, [isAnswered, submittedAnswers, questions, localAnswers]);

  const handleOptionClick = useCallback((questionIdx: number, optionIdx: number, isMultiSelect: boolean) => {
    setLocalAnswers(prev => {
      const current = prev[questionIdx] || { selected_options: [], other_text: "" };

      if (isMultiSelect) {
        // Toggle selection for multi-select
        const isSelected = current.selected_options.includes(optionIdx);
        const newSelected = isSelected
          ? current.selected_options.filter(i => i !== optionIdx)
          : [...current.selected_options, optionIdx];
        return {
          ...prev,
          [questionIdx]: { ...current, selected_options: newSelected },
        };
      } else {
        // Single select - replace selection and clear "other" text (mutually exclusive)
        return {
          ...prev,
          [questionIdx]: { selected_options: [optionIdx], other_text: "" },
        };
      }
    });
  }, []);

  const handleOtherTextChange = useCallback((questionIdx: number, text: string, isMultiSelect: boolean) => {
    setLocalAnswers(prev => {
      const current = prev[questionIdx] || { selected_options: [], other_text: "" };
      if (isMultiSelect) {
        // Multi-select: "other" can coexist with options
        return {
          ...prev,
          [questionIdx]: { ...current, other_text: text },
        };
      } else {
        // Single-select: "other" is mutually exclusive with options
        return {
          ...prev,
          [questionIdx]: { selected_options: [], other_text: text },
        };
      }
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const formattedAnswers: QuestionAnswerType[] = questions.map((_, idx) => {
      const answer = localAnswers[idx] || { selected_options: [], other_text: "" };
      return {
        question_index: idx,
        selected_options: answer.selected_options,
        other_text: answer.other_text || undefined,
      };
    });
    onSubmit(formattedAnswers);
  }, [localAnswers, questions, onSubmit]);

  // Check if ALL questions have at least one answer (selection or other text)
  const hasAnswer = questions.every((_, idx) => {
    const answer = displayAnswers[idx];
    return (answer?.selected_options?.length > 0) || (answer?.other_text?.trim());
  });

  if (questions.length === 0) {
    return null;
  }

  const isDisabled = isSubmitting || isTimedOut || isAnswered;

  // Determine header text and styling
  const getHeaderContent = () => {
    if (isTimedOut) {
      return { text: "Question timed out", icon: <AlertCircle className="h-5 w-5" />, colorClass: "text-gray-400" };
    }
    if (isAnswered) {
      return { text: "Question answered", icon: <Check className="h-5 w-5" />, colorClass: "text-green-400" };
    }
    return { text: `${productName} needs your input`, icon: <HelpCircle className="h-5 w-5" />, colorClass: "text-primary" };
  };

  const headerContent = getHeaderContent();

  // Container styling based on state and embedded mode
  const containerClass = cn(
    "flex flex-col gap-4 rounded-lg border p-4",
    embedded ? "my-2" : "my-2 md:ml-10 md:mr-12",
    isTimedOut
      ? "border-border/40 bg-gray-900/20"
      : isAnswered
        ? "border-green-800/40 bg-green-950/20"
        : "border-blue-800/40 bg-blue-950/20"
  );

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className={cn("flex items-center gap-2", headerContent.colorClass)}>
        {headerContent.icon}
        <span className="font-medium">{headerContent.text}</span>
      </div>

      {/* Questions */}
      {questions.map((q, questionIdx) => (
        <QuestionItem
          key={questionIdx}
          question={q}
          questionIdx={questionIdx}
          answer={displayAnswers[questionIdx]}
          onOptionClick={handleOptionClick}
          onOtherTextChange={handleOtherTextChange}
          disabled={isDisabled}
        />
      ))}

      {/* Submit button, timeout message, or nothing for answered */}
      {!isAnswered && (
        <div className="flex justify-end">
          {isTimedOut ? (
            <div className="text-sm text-gray-400">
              Please re-ask your question to continue.
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !hasAnswer}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
                hasAnswer && !isSubmitting
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-700 text-gray-400 cursor-not-allowed"
              )}
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner className="h-4 w-4" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Submit Answer
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface QuestionItemProps {
  question: QuestionType;
  questionIdx: number;
  answer: { selected_options: number[]; other_text: string };
  onOptionClick: (questionIdx: number, optionIdx: number, isMultiSelect: boolean) => void;
  onOtherTextChange: (questionIdx: number, text: string, isMultiSelect: boolean) => void;
  disabled: boolean;
}

const QuestionItem: React.FC<QuestionItemProps> = React.memo(function QuestionItem({
  question,
  questionIdx,
  answer,
  onOptionClick,
  onOtherTextChange,
  disabled,
}) {
  const safeAnswer = answer ?? { selected_options: [], other_text: "" };
  const isMultiSelect = question.multiSelect ?? false;
  const hasOtherText = safeAnswer.other_text.trim().length > 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Question header/chip */}
      {question.header && (
        <div className="flex">
          <span className="px-2 py-1 text-sm font-semibold bg-primary/20 text-primary rounded">
            {question.header}
          </span>
        </div>
      )}

      {/* Question text */}
      <MarkdownPreview className="text-sm text-foreground">{question.question}</MarkdownPreview>

      {/* Multi-select hint */}
      {isMultiSelect && (
        <p className="text-xs text-muted-foreground">Select all that apply</p>
      )}

      {/* Options - compact layout with label and description inline */}
      <div className="flex flex-col gap-1.5">
        {question.options.map((option, optionIdx) => {
          const isSelected = safeAnswer.selected_options.includes(optionIdx);
          return (
            <button
              key={optionIdx}
              onClick={() => onOptionClick(questionIdx, optionIdx, isMultiSelect)}
              disabled={disabled}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all border text-left",
                isSelected
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-muted border-border text-foreground hover:bg-muted/80 hover:border-border",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <span className="font-medium">{option.label}</span>
              {option.description && (
                <span className={cn(
                  "text-xs",
                  isSelected ? "text-primary/80" : "text-gray-500"
                )}>
                  — {option.description}
                </span>
              )}
            </button>
          );
        })}

        {/* Other option - displayed as an option in the list */}
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all border",
            hasOtherText
              ? "bg-blue-600 border-blue-500"
              : "bg-gray-800 border-border",
            disabled && "opacity-50"
          )}
        >
          <span className={cn(
            "font-medium flex-shrink-0",
            hasOtherText ? "text-white" : "text-foreground"
          )}>
            Other:
          </span>
          <input
            type="text"
            value={safeAnswer.other_text}
            onChange={(e) => onOtherTextChange(questionIdx, e.target.value, isMultiSelect)}
            disabled={disabled}
            placeholder="Type your answer..."
            className={cn(
              "flex-1 px-2 py-0.5 rounded text-sm bg-gray-900/50 border border-border text-foreground",
              "placeholder:text-gray-500 focus:outline-none focus:border-blue-400",
              disabled && "cursor-not-allowed"
            )}
          />
        </div>
      </div>
    </div>
  );
});
